// covered-by: frontends/carrie/src/components/__tests__/Markdown.test.tsx

/**
 * The inline claim citation: the pack's wording, then the source badge.
 *
 * Its own module, not another block appended to `Markdown.tsx`: that renderer is
 * the app-wide leaf every chat message and review comment mounts and was already
 * far past the 400-line expert-legibility bar before this existed. This half is
 * self-contained -- it reads one matched claim and renders the wording plus the
 * badge that opens the source hover card -- so `Markdown.tsx` keeps only the
 * composition: the tag -> renderer entry and the plugin it comes from.
 *
 * Sibling of `Markdown.tsx` rather than a nested folder so the module paths this
 * file reaches for (`../ask-v2/paths`, `../components/ask-puntt-sources`) are the
 * ones it had, and the move changes no resolution.
 */

import { Link as RouterLink } from "@tanstack/react-router";
import {
  type ReactNode,
  Suspense,
  createContext,
  lazy,
  useContext,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { SourceHoverCard } from "./ask-puntt-sources";
import { type AskPunttSource } from "./ask-puntt-sources/types";

import { ASK_V2_CHAT_PATH } from "../ask-v2/paths";
import rehypeClaimCitation, {
  type ClaimCitationOptions,
  type LinkableClaim,
} from "../plugins/rehype-claim-citation";
import {
  type TicketMessageCitation,
  cellAnchorLabel,
  isKnowledgeRuleCitation,
} from "../utils/citations";
import { store } from "../utils/store";

/**
 * `isPunttGuest` (`utils/auth.ts`) with the same predicate. why-not that
 * import: auth.ts pulls in the login route module, and Markdown is the leaf
 * every surface mounts.
 */
function isShareLinkGuest(): boolean {
  const user = store.getState().auth.value;
  return (
    user != null && user.role == null && !!(user.ticketId || user.folderId)
  );
}

// why lazy: Markdown renders on every surface, and the dialog brings the PDF
// and spreadsheet viewers with it. Only a clicked badge pays for them.
const KnowledgeRuleCitationDialog = lazy(() =>
  import("./KnowledgeRuleCitationDialog").then((module) => ({
    default: module.KnowledgeRuleCitationDialog,
  })),
);

/**
 * The claims the body being rendered can cite. The hast node carries only
 * strings, so a badge reaches its stored citation through this by rule id.
 */
export const ClaimCitationsContext = createContext<readonly LinkableClaim[]>(
  [],
);

const CHIP_TITLE_MAX = 64;

type CitationFields = {
  ruleId: string;
  statement?: string;
  memoryId?: string;
  memoryLabel?: string;
  chatId?: string;
  chatLabel?: string;
  index?: number;
};

function present(value?: string): string {
  return value?.trim() ?? "";
}

function clipTitle(raw: string): string {
  if (raw.length <= CHIP_TITLE_MAX) return raw;
  return `${raw.slice(0, CHIP_TITLE_MAX - 3)}...`;
}

type SourceDetails = Pick<
  AskPunttSource,
  "sourceName" | "locationLabel" | "excerpt"
>;

/**
 * What the rule dialog shows beyond the claim: the document, where in it, and
 * the verbatim text the rule was read from.
 *
 * why the guest gate: share-link guests have no Knowledge Database access, and
 * the dialog's `allowSourcePreview` hides exactly these three for them.
 */
function citationDetails(
  citation: TicketMessageCitation,
  pageLabel: (page: number) => string,
  guest: boolean,
): SourceDetails {
  if (guest || !isKnowledgeRuleCitation(citation)) return {};
  const location = citation.location;
  const locationLabel = location?.cellAnchor
    ? cellAnchorLabel(location.cellAnchor)
    : location?.pageNumber
      ? pageLabel(location.pageNumber)
      : "";
  const sourceName = present(citation.sourceName);
  const excerpt = present(citation.quote);
  return {
    ...(sourceName ? { sourceName } : {}),
    ...(locationLabel ? { locationLabel } : {}),
    ...(excerpt ? { excerpt } : {}),
  };
}

/**
 * The hover card names the record the claim rests on: the memory or chat when
 * the graph reached one, otherwise the claim. The claim itself rides as the
 * summary whenever the title is something else, and the source details follow.
 *
 * why no href: SourceTitleLink opens href in a new tab, and this citation must
 * stay on the hover card instead of leaving for the knowledge database.
 */
function citationSource(
  fields: CitationFields,
  details: SourceDetails,
): AskPunttSource {
  const claim = present(fields.statement);
  const memory = fields.memoryId ? present(fields.memoryLabel) : "";
  const chat = !memory && fields.chatId ? present(fields.chatLabel) : "";
  const title =
    memory ||
    chat ||
    claim ||
    (fields.index ? `Citation ${fields.index}` : "Source");
  // A quote that only repeats the claim already on the card adds nothing.
  const { excerpt, ...rest } = details;
  const repeatsClaim =
    excerpt != null && excerpt.toLowerCase() === claim.toLowerCase();
  return {
    id: fields.ruleId,
    title,
    kind: "knowledge",
    ...(claim && claim !== title ? { summary: claim } : {}),
    ...rest,
    ...(excerpt && !repeatsClaim ? { excerpt } : {}),
  };
}

/**
 * The badge text is the citation's number: the chips under the message and the
 * rail's Cited claims list are numbered over the same list, so "3" here is
 * chip 3 there. A claim with no position falls back to its clipped title.
 *
 * Hover previews the source; a click opens the rule dialog, the same one the
 * rail's Cited claims card opens.
 */
function CitationBadge({
  source,
  index,
  testId,
  citation,
  guest,
}: {
  source: AskPunttSource;
  index?: number;
  testId: string;
  citation: TicketMessageCitation;
  guest: boolean;
}) {
  const { t } = useTranslation("ui");
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <span className="ml-1 inline-flex align-middle">
      <SourceHoverCard
        sources={[source]}
        testIdPrefix={`claim-citation-${testId}`}
        groupId={testId}
        trigger="chip"
        density="cell"
        chipLabel={index ? String(index) : clipTitle(source.title)}
        // The footer chip's name for the same claim, so both announce alike.
        chipAriaLabel={
          index
            ? t("citedClaimsPanel.chipAriaLabel", {
                n: index,
                statement: source.summary ?? source.title,
              })
            : undefined
        }
        onTriggerClick={() => setDialogOpen(true)}
      />
      {dialogOpen ? (
        <Suspense fallback={null}>
          <KnowledgeRuleCitationDialog
            citation={citation}
            open
            onOpenChange={setDialogOpen}
            showKnowledgeDatabaseLink={!guest}
            allowSourcePreview={!guest}
          />
        </Suspense>
      ) : null}
    </span>
  );
}

/**
 * The quoted claim, with the source badge appended after the words.
 *
 * The wording is not a link to the knowledge database: the badge is the
 * citation, and hovering it opens the source card. A conversation is the one
 * destination that stays a link, because that record lives in the chat, not
 * in the registry.
 */
export default function ClaimCitationLink({
  ruleId,
  sourceId,
  chatId,
  chatLabel,
  memoryId,
  memoryLabel,
  index,
  scope,
  occurrence,
  marker,
  label,
  statement,
  children,
}: {
  ruleId?: string;
  sourceId?: string;
  cite?: string;
  chatId?: string;
  chatLabel?: string;
  memoryId?: string;
  memoryLabel?: string;
  index?: number;
  scope?: string;
  occurrence?: number;
  marker?: boolean;
  label?: string;
  statement?: string;
  children?: ReactNode;
}) {
  const claims = useContext(ClaimCitationsContext);
  const { t } = useTranslation("ui");
  if (!ruleId) return <>{children}</>;

  // Scoped so the id resolves to exactly one node (testid-a11y): `scope` keeps
  // two comment bodies apart on a review page that renders many in one pass,
  // and `occurrence` keeps two mentions of the same rule apart inside one body.
  // Both are suffixes only when they are needed, so a single mention in a
  // standalone body stays the bare rule id.
  const testId = [
    ruleId,
    scope || null,
    occurrence && occurrence > 1 ? String(occurrence) : null,
  ]
    .filter(Boolean)
    .join("-");

  const stored = claims.find((claim) => claim.ruleId === ruleId)?.citation;
  const guest = isShareLinkGuest();
  // why a guest reads the stored statement only: the plugin writes the quote
  // into `statement` and `label` when a rule has no rewritten claim, and the
  // quote is the source text a share-link guest may not see.
  const claimText = guest
    ? stored && isKnowledgeRuleCitation(stored)
      ? present(stored.statement)
      : ""
    : present(statement) || present(label);
  // A caller that renders claims without their stored record still gets a
  // dialog, naming the claim without the source pin.
  const citation: TicketMessageCitation = stored ?? {
    ruleId,
    ...(sourceId ? { sourceId } : {}),
    statement: claimText,
  };
  const source = citationSource(
    {
      ruleId,
      statement: claimText,
      memoryId,
      memoryLabel,
      chatId,
      chatLabel,
      index,
    },
    citationDetails(
      citation,
      (page) => t("askPuntt.sources.pageLabel", { page }),
      guest,
    ),
  );
  const badge = (
    <CitationBadge
      source={source}
      index={index}
      testId={testId}
      citation={citation}
      guest={guest}
    />
  );

  // A `[n]` marker sits where the superscript used to: the numbered badge
  // replaces it, appended after the sentence.
  if (marker) {
    return <span data-testid={`claim-citation-marker-${testId}`}>{badge}</span>;
  }

  const chatTitle = chatLabel
    ? `Discussed in "${chatLabel}"`
    : "Discussed in an earlier conversation";

  // A claim can be BOTH recorded and discussed. The badge names the memory;
  // the conversation rides beside it as a small trailing word. Rendered as a
  // SIBLING of the wording, never nested: an anchor inside an anchor is
  // invalid HTML and browsers unnest it.
  const aside =
    memoryId && chatId ? (
      <RouterLink
        data-testid={`claim-citation-chat-aside-${testId}`}
        to={ASK_V2_CHAT_PATH}
        params={{ chatId }}
        title={chatTitle}
        aria-label={chatTitle}
        className="puntt-link-chat ml-0.5 align-super text-[0.7em] font-semibold no-underline"
      >
        chat
      </RouterLink>
    ) : null;

  // why the badge is a sibling of the wording node: the sentence keeps the
  // pack's own words, and the chip is the hover target appended after them.
  if (memoryId) {
    return (
      <>
        <span
          data-testid={`claim-citation-memory-link-${testId}`}
          className="puntt-link-memory cursor-auto underline decoration-dotted underline-offset-2"
        >
          {children}
        </span>
        {badge}
        {aside}
      </>
    );
  }

  if (chatId) {
    return (
      <>
        <RouterLink
          data-testid={`claim-citation-chat-link-${testId}`}
          to={ASK_V2_CHAT_PATH}
          params={{ chatId }}
          title={chatTitle}
          aria-label={chatTitle}
          className="puntt-link-chat underline decoration-dotted underline-offset-2"
        >
          {children}
        </RouterLink>
        {badge}
      </>
    );
  }

  return (
    <>
      <span
        data-testid={`claim-citation-link-${testId}`}
        className="puntt-link cursor-auto underline decoration-dotted underline-offset-2"
      >
        {children}
      </span>
      {badge}
    </>
  );
}

/**
 * The rehype plugin entry that emits the tag this component renders.
 *
 * Here beside the renderer because the two are one contract: the plugin writes
 * `CLAIM_CITATION_TAG` nodes carrying exactly the props above, and nothing else
 * in the app produces or consumes them. `Markdown.tsx` calls this only when a
 * caller passed claims, so a body with none never constructs the plugin.
 */
export function claimCitationRehypePlugin(
  claims: LinkableClaim[],
  scope?: string,
): [typeof rehypeClaimCitation, ClaimCitationOptions] {
  return [rehypeClaimCitation, { claims, scope }];
}
